import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import Button from './ui/Button';
import Select from './ui/Select';
import { useStudents } from 'context/StudentsContext';
import { useIEPGoals } from 'context/IEPGoalsContext';

// ---------------------------------------------------------------------------
// CollectDataLauncher
//
// The one-tap "Collect Data" modal. A teacher or paraprofessional in the
// middle of a moment — a kid melting down, a data-collection window
// closing, a hurried between-classes moment — should not have to think
// about where to go. This modal is the answer.
//
// Two flows:
//
//   1. Behavior incident. Pick student → open Behavioral Incident Logging
//      with the student preselected and the Log Incident tab active.
//
//   2. IEP goal data point. Pick student → pick goal → open IEP Goals with
//      the goal detail view open, scrolled to the "Log a data point" form.
//
// Both flows use react-router's navigate + location.state so no URL
// scheme changes are required. The destination pages read the state on
// mount and preselect / auto-open accordingly.
//
// Props:
//   isOpen        boolean
//   onClose       () => void
//   modes         Array<'behavior' | 'iep'>  (default: both). If only one
//                 is passed, the picker skips the "what are you logging?"
//                 step and just shows that flow's student/goal pickers.
//   defaultStudentId?  string
// ---------------------------------------------------------------------------

const CollectDataLauncher = ({
  isOpen,
  onClose,
  modes = ['behavior', 'iep'],
  defaultStudentId = '',
}) => {
  const navigate = useNavigate();
  const { students } = useStudents();
  const { goals } = useIEPGoals();

  // If only one flow is available, there's no top-level picker; we render
  // just the student/goal chooser for that flow.
  const singleMode = modes.length === 1 ? modes[0] : null;

  const [flow, setFlow] = useState(singleMode); // 'behavior' | 'iep' | null
  const [studentId, setStudentId] = useState(defaultStudentId || '');
  const [goalId, setGoalId] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    // Reset picker state whenever the launcher opens so it doesn't remember
    // half-filled selections from a previous click.
    setFlow(singleMode);
    setStudentId(defaultStudentId || '');
    setGoalId('');
  }, [isOpen, singleMode, defaultStudentId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const activeStudents = useMemo(
    () =>
      (Array.isArray(students) ? students : [])
        .filter((s) => (s.status || 'active') !== 'archived')
        .sort((a, b) => {
          const an = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
          const bn = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
          return an.localeCompare(bn);
        }),
    [students],
  );

  const studentOptions = useMemo(
    () =>
      activeStudents.map((s) => ({
        value: s.id,
        label:
          `${s.firstName || ''} ${s.lastName || ''}`.trim() ||
          'Unnamed student',
      })),
    [activeStudents],
  );

  // Goals for the picked student. Only "still on the roadmap" statuses —
  // it's rarely useful to log new data against a mastered or discontinued
  // goal from this launcher.
  const goalOptionsForStudent = useMemo(() => {
    if (!studentId) return [];
    return (Array.isArray(goals) ? goals : [])
      .filter((g) => g.studentId === studentId)
      .filter((g) => {
        const status = g.status || 'in_progress';
        return status === 'in_progress' || status === 'on_hold';
      })
      .map((g) => ({
        value: g.id,
        label: goalLabel(g),
      }));
  }, [goals, studentId]);

  const handleStartBehavior = () => {
    if (!studentId) return;
    navigate('/behavioral-incident-logging', {
      state: { selectedStudentId: studentId },
    });
    onClose?.();
  };

  const handleStartIep = () => {
    if (!goalId) return;
    navigate('/iep-goals-and-objectives-tracking', {
      state: { openGoalId: goalId },
    });
    onClose?.();
  };

  if (!isOpen) return null;

  // z-[1100]: the app's fixed header is z-[1000] (see src/components/ui/Header.jsx)
  // and the sidebar is z-[900]. If this modal were the default z-50 (which it
  // used to be), the header would sit ABOVE the overlay: it stayed fully lit,
  // still intercepted taps on the top ~64 px of the viewport, and the mobile
  // sidebar could even open on top of the modal. Any modal that expects to
  // capture attention has to clear z-[1000].
  //
  // overflow-visible on the dialog card (not overflow-hidden): the <Select>
  // dropdown inside the modal is `position: absolute` and opens *downward*.
  // On a mobile viewport there's typically <120 px of space below the
  // student/goal picker, so the dropdown extends past the card's bottom
  // edge. If the card has overflow-hidden the whole option list is clipped
  // and the dropdown appears to do nothing when you tap it — which is
  // exactly what "these are not working and dropdowns inside them not
  // working" looks like on a phone.
  // Single-scroll-container pattern — outer wrapper is the ONLY thing
  // that scrolls. Fixes the iOS Safari nested-scroll bug where the user
  // can't reach the top of a tall modal.
  const overlayDismiss = (e) => {
    if (e.target === e.currentTarget) onClose?.();
  };

  return (
    <div
      className="fixed inset-0 z-[1100] bg-black/40 overflow-y-auto overscroll-contain"
      onClick={overlayDismiss}
    >
      <div
        className="min-h-full flex items-start sm:items-center justify-center p-4"
        onClick={overlayDismiss}
      >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="collect-data-title"
        className="w-full max-w-lg rounded-xl bg-white shadow-xl my-4 sm:my-8"
      >
        <div className="flex items-start justify-between border-b border-gray-200 p-5">
          <div>
            <h2
              id="collect-data-title"
              className="text-lg font-semibold text-gray-900"
            >
              {flow === 'behavior'
                ? 'Log a behavior incident'
                : flow === 'iep'
                ? 'Log IEP goal data'
                : 'Collect data'}
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              {flow
                ? 'Pick who, then go straight to the recording screen.'
                : 'One tap to record — pick what you’re logging.'}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
          >
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Step 1: pick a flow (skipped if the caller locked us to one) */}
          {!flow && (
            <div className="grid grid-cols-1 gap-3">
              <FlowCard
                icon="AlertTriangle"
                iconColor="text-amber-600"
                iconBg="bg-amber-50"
                title="Behavior incident"
                subtitle="ABC entry: antecedent → behavior → consequence."
                onClick={() => setFlow('behavior')}
              />
              <FlowCard
                icon="Target"
                iconColor="text-primary"
                iconBg="bg-primary/10"
                title="IEP goal data point"
                subtitle="Record a value against a goal (e.g. trials, %, count)."
                onClick={() => setFlow('iep')}
              />
            </div>
          )}

          {/* Step 2: behavior flow — pick a student */}
          {flow === 'behavior' && (
            <div className="space-y-4">
              {activeStudents.length === 0 ? (
                <EmptyRoster />
              ) : (
                <>
                  <Select
                    label="Student"
                    placeholder="Pick a student"
                    options={studentOptions}
                    value={studentId}
                    onChange={(v) => setStudentId(v)}
                    searchable
                    required
                  />
                  <div className="flex items-center justify-between gap-2">
                    {!singleMode && (
                      <Button
                        variant="ghost"
                        onClick={() => setFlow(null)}
                        iconName="ChevronLeft"
                        iconPosition="left"
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      variant="default"
                      onClick={handleStartBehavior}
                      disabled={!studentId}
                      iconName="Play"
                      iconPosition="left"
                      className="ml-auto"
                    >
                      Log incident
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 2: IEP flow — pick a student, then a goal */}
          {flow === 'iep' && (
            <div className="space-y-4">
              {activeStudents.length === 0 ? (
                <EmptyRoster />
              ) : (
                <>
                  <Select
                    label="Student"
                    placeholder="Pick a student"
                    options={studentOptions}
                    value={studentId}
                    onChange={(v) => {
                      setStudentId(v);
                      setGoalId('');
                    }}
                    searchable
                    required
                  />
                  {studentId && goalOptionsForStudent.length === 0 ? (
                    <div className="rounded-lg border border-dashed border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                      No active goals for this student yet. Add a goal first
                      from the IEP Goals page.
                    </div>
                  ) : (
                    <Select
                      label="Goal"
                      placeholder={
                        studentId ? 'Pick a goal' : 'Pick a student first'
                      }
                      options={goalOptionsForStudent}
                      value={goalId}
                      onChange={(v) => setGoalId(v)}
                      disabled={!studentId}
                      searchable
                      required
                    />
                  )}
                  <div className="flex items-center justify-between gap-2">
                    {!singleMode && (
                      <Button
                        variant="ghost"
                        onClick={() => setFlow(null)}
                        iconName="ChevronLeft"
                        iconPosition="left"
                      >
                        Back
                      </Button>
                    )}
                    <Button
                      variant="default"
                      onClick={handleStartIep}
                      disabled={!goalId}
                      iconName="Play"
                      iconPosition="left"
                      className="ml-auto"
                    >
                      Open goal
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};

// Short goal label used in the goal picker. Prefer the goal area
// ("reading", "behavior") plus the first ~50 chars of the goal statement;
// full "specific" text can be several sentences long which crashes a
// dropdown row layout.
function goalLabel(g) {
  const area = g.goalArea ? capitalize(g.goalArea) : '';
  const spec = g.specific ? String(g.specific).trim() : '';
  const short = spec.length > 60 ? `${spec.slice(0, 60)}…` : spec;
  if (area && short) return `${area} · ${short}`;
  return area || short || `Goal ${String(g.id).slice(0, 6)}`;
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const FlowCard = ({ icon, iconColor, iconBg, title, subtitle, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full text-left flex items-start gap-4 rounded-xl border border-gray-200 p-4 hover:border-primary hover:bg-primary/5 focus:outline-none focus:ring-2 focus:ring-primary/40 transition-colors"
  >
    <div
      className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}
    >
      <Icon name={icon} size={24} className={iconColor} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-gray-900">{title}</p>
      <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>
    </div>
    <Icon
      name="ChevronRight"
      size={18}
      className="text-gray-400 flex-shrink-0 mt-3"
    />
  </button>
);

const EmptyRoster = () => (
  <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-4 text-center">
    <p className="text-sm font-medium text-gray-800">No students on your roster yet</p>
    <p className="text-xs text-gray-500 mt-1">
      Add a student first — the dashboard has an "Add Student" button.
    </p>
  </div>
);

export default CollectDataLauncher;
